import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Truck, Plus, ExternalLink, CheckSquare, Square, Pencil, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState, Pill } from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";
import ClientRefsCard from "./ClientRefsCard";   // mig 480 — the client's numbers for this job
import {
  type DeliveryRow, DELIVERY_SELECT, SOURCE_LABEL, LINE_STATUS_LABEL,
  docketSignedUrl, markTicked, deleteDelivery,
} from "./deliveries";

// =============================================================================
// ProjectMaterialsTab — the Materials tab on a project (mig 382)
// =============================================================================
// Every delivery / pickup matched to this job, newest first, with its lines.
// A manager ticks "ticked in client sheet" once they have marked the lines in
// the client's tracker (APD's Excel — Ops tells a person what to tick, it
// never writes the client's sheet: decision 19 Sep 2026). Field users see
// the deliveries for jobs they are on; only manager+ tick or delete.
// =============================================================================

export default function ProjectMaterialsTab({ projectId }: { projectId: string }) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["project_deliveries", projectId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("material_deliveries").select(DELIVERY_SELECT)
        .eq("project_id", projectId).order("received_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DeliveryRow[];
    },
  });

  const lineCount = rows.reduce((n, r) => n + (r.lines?.length ?? 0), 0);
  const unticked = rows.filter(r => r.status !== "ticked_in_client_sheet").length;

  return (
    <div className="space-y-4">
      <ClientRefsCard projectId={projectId} canEdit={canManage} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] text-text-muted">
          {rows.length} deliver{rows.length === 1 ? "y" : "ies"} · {lineCount} line{lineCount === 1 ? "" : "s"}
          {unticked > 0 && canManage && <span className="text-warn"> · {unticked} not yet ticked in the client sheet</span>}
        </div>
        <Link to={`/capture/delivery?project=${projectId}`}><Button size="sm" icon={Plus}>Take a delivery</Button></Link>
      </div>
      {isLoading && <Spinner />}
      {!isLoading && rows.length === 0 && (
        <EmptyState icon={Truck} title="No deliveries logged" description="Snap the docket when materials come in and it lands here against the job." />
      )}
      {rows.map(r => <DeliveryCard key={r.delivery_id} row={r} canManage={canManage} invalidateKeys={[["project_deliveries", projectId], ["deliveries"]]} />)}
    </div>
  );
}

// Shared by the project tab and the register.
export function DeliveryCard({ row: r, canManage, invalidateKeys, showProject = false, compact = false }: {
  row: DeliveryRow; canManage: boolean; invalidateKeys: unknown[][]; showProject?: boolean; compact?: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ticked = r.status === "ticked_in_client_sheet";
  const invalidate = () => invalidateKeys.forEach(k => qc.invalidateQueries({ queryKey: k }));

  async function openDoc(path: string) {
    const url = await docketSignedUrl(path);
    if (url) window.open(url, "_blank", "noopener");
  }
  async function toggleTick() {
    setBusy(true);
    try { await markTicked(r.delivery_id, !ticked); invalidate(); }
    catch (e: any) { window.alert(`Couldn't update: ${e?.message ?? e}`); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm("Delete this delivery and its lines? The docket file stays in storage.")) return;
    setBusy(true);
    try { await deleteDelivery(r.delivery_id); invalidate(); }
    catch (e: any) { window.alert(`Couldn't delete: ${e?.message ?? e}`); }
    finally { setBusy(false); }
  }

  const lines = (r.lines ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
  const flagged = lines.filter(l => l.line_status !== "received").length;
  const who = r.received_by ? `${r.received_by.first_name} ${r.received_by.last_name}` : null;

  return (
    <Card>
      <div className="p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-[14px]">{fmtDateTime(r.received_at)}</span>
              <Pill small tone={ticked ? "ok" : r.status === "matched" ? "notice" : "pending"} label={ticked ? "ticked in client sheet" : r.status === "matched" ? "matched" : "not matched"} />
              {flagged > 0 && <Pill small tone="warning" label={`${flagged} flagged`} />}
              {r.source !== "depot_delivery" && <Pill small tone="default" label={SOURCE_LABEL[r.source]} />}
            </div>
            <div className="text-[12px] text-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {showProject && (r.project
                ? <Link to={`/projects/${r.project_id}?tab=materials`} className="text-accent hover:underline">{r.project.project_code} · {r.project.project_name}</Link>
                : <span className="text-warn">no job matched{r.client_site_name ? ` · slip says ${r.client_site_name}` : ""}</span>)}
              {r.client_project_ref && <span className="font-mono">{r.client_project_ref}</span>}
              {r.client_site_code && <span className="font-mono">{r.client_site_code}</span>}
              {r.supplier && <span>{r.supplier}{r.carrier ? ` via ${r.carrier}` : ""}</span>}
              {r.docket_number && <span>docket {r.docket_number}</span>}
              {who && <span>received by {who}</span>}
              {r.signed_by && <span>signed {r.signed_by}</span>}
            </div>
            {r.notes && <div className="text-[12px] mt-1.5">{r.notes}</div>}
          </div>
          <div className="flex items-center gap-1">
            {r.document_paths.map((p, i) => (
              <button key={p} type="button" onClick={() => openDoc(p)} className="p-1.5 text-text-muted hover:text-text" title={`Open docket ${i + 1}`}><ExternalLink size={14} /></button>
            ))}
            <Link to={`/capture/delivery?id=${r.delivery_id}`} className="p-1.5 text-text-muted hover:text-text" title="Edit"><Pencil size={14} /></Link>
            {canManage && <button type="button" onClick={remove} disabled={busy} className="p-1.5 text-text-muted hover:text-danger" title="Delete"><Trash2 size={14} /></button>}
            <button type="button" onClick={() => setOpen(o => !o)} className="p-1.5 text-text-muted hover:text-text inline-flex items-center gap-1 text-[11px]" title={open ? "Hide lines" : "Show lines"}>{lines.length > 0 && !open && <span>{lines.length} line{lines.length === 1 ? "" : "s"}</span>}{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
          </div>
        </div>

        {canManage && (
          <button type="button" onClick={toggleTick} disabled={busy || (!ticked && !r.project_id)}
            className={`mt-3 inline-flex items-center gap-2 text-[12px] ${ticked ? "text-ok" : "text-text-muted hover:text-text"} disabled:opacity-50`}
            title={!ticked && !r.project_id ? "Match it to a job first" : ""}>
            {ticked ? <CheckSquare size={15} /> : <Square size={15} />}
            {ticked ? `Ticked in the client's sheet ${r.ticked_at ? fmtDateTime(r.ticked_at) : ""}` : "Mark as ticked in the client's sheet"}
          </button>
        )}

        {(open || (!compact && lines.length <= 4)) && lines.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
                  <th className="py-1 pr-3">#</th><th className="py-1 pr-3">Part no.</th><th className="py-1 pr-3">Description</th>
                  <th className="py-1 pr-3 text-right">Slip</th><th className="py-1 pr-3 text-right">Recd</th><th className="py-1 pr-3">Status</th><th className="py-1">Note / serials</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.line_id} className="border-t border-line/60 align-top">
                    <td className="py-1.5 pr-3 text-text-dim font-mono">{i + 1}</td>
                    <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{l.part_number ?? "—"}{l.client_material_no && l.client_material_no !== l.part_number ? <div className="text-text-muted">{l.client_material_no}</div> : null}</td>
                    <td className="py-1.5 pr-3">{l.description ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{l.qty_expected ?? "—"}{l.uom ? <span className="text-text-muted"> {l.uom}</span> : null}</td>
                    <td className={`py-1.5 pr-3 text-right font-mono ${l.qty_received != null && l.qty_expected != null && l.qty_received < l.qty_expected ? "text-warn" : ""}`}>{l.qty_received ?? "—"}</td>
                    <td className="py-1.5 pr-3">{l.line_status === "received" ? <span className="text-text-muted">ok</span> : <Pill small tone="warning" label={LINE_STATUS_LABEL[l.line_status]} />}</td>
                    <td className="py-1.5 text-text-muted">{[l.note, l.serials?.length ? `S/N ${l.serials.join(", ")}` : null].filter(Boolean).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!open && lines.length > 4 && <button type="button" onClick={() => setOpen(true)} className="mt-2 text-[12px] text-accent hover:underline">Show {lines.length} lines</button>}
      </div>
    </Card>
  );
}
